<template>
  <div class="bi-competitor-summary">
    <div class="__header">
      <BaseDatePicker
        variant="full"
        @on-save-dates="updateDateRange"
        :startDate="dates[0]"
        :endDate="dates[1]"
        :datePickerValues="datePickerPeriod"
      />
    </div>
    <div class="__content">
      <div class="__highlighted">
        <BaseLoadingValue :state-loading="!competitorName">
          <template slot="content">
            <div class="__title">{{ competitorName }}</div>
          </template>
        </BaseLoadingValue>
        <BISummarySection :data="competitorSummary" :allowEdit="true" @on-edit-clicked="onEditClicked" />
      </div>
      <div>
        <BaseLoadingValue :state-loading="!tenantName">
          <template slot="content">
            <div class="__title __highlighted">{{ tenantName }}</div>
          </template>
        </BaseLoadingValue>
        <BISummarySection :data="tenantSummary" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { bus } from '@/main';
import BISummarySection from './BISummarySection.vue';
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';
import BaseDatePicker from '@/components/base/BaseDatePicker.vue';
import { calculatePercentage, formatPercentage } from '@/util/number-util';
import { formatCurrencyShort } from '@/util/currency-util';
import { formatStandardDate } from '@/util/date-util';
import { BI_DATE_PICKER_PERIOD } from '@/util/constants/date-picker-periods';

export default {
  name: 'BICompetitorSummary',
  components: {
    BaseDatePicker,
    BaseLoadingValue,
    BISummarySection,
  },
  props: {
    dates: {
      type: Array,
      required: true,
    },
  },
  watch: {
    dates() {
      this.getCompetitorPerformanceSummary({
        competitorId: this.competitorId,
        startDate: this.dates[0],
        endDate: this.dates[1],
      });
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['competitorPerformanceSummary']),
    ...mapGetters('userPreferences', ['userPreferences']),
    competitorId() {
      return this.$route.params.competitorId;
    },
    tenantName() {
      return this.userPreferences ? this.userPreferences.tenantName : '';
    },
    competitorName() {
      // get from route if available, otherwise from store after loading the summary
      return this.competitorSummary
        ? this.competitorSummary.name
        : this.$route.query.name
        ? this.$route.query.name
        : undefined;
    },
    competitorSummary() {
      if (!this.competitorPerformanceSummary) return null;
      return this.competitorPerformanceSummary.competitor;
    },
    tenantSummary() {
      if (!this.competitorPerformanceSummary) return null;
      return this.competitorPerformanceSummary.tenant;
    },
    awardsData() {
      // display a straight line if no data or less than 2 data points
      if (!this.competitorSummary || this.competitorSummary.current_period.history.length === 0) {
        return [0, 0];
      }
      if (this.competitorSummary.current_period.history.length === 1) {
        return [
          this.competitorSummary.current_period.history[0].amount,
          this.competitorSummary.current_period.history[0].amount,
        ];
      }
      return this.competitorSummary.current_period.history.map((award) => award.amount);
    },
    datePickerPeriod() {
      return BI_DATE_PICKER_PERIOD;
    },
  },
  methods: {
    ...mapActions('biDashboard', ['getCompetitorPerformanceSummary']),
    calculatePercentage,
    formatPercentage,
    formatCurrencyShort,
    formatStandardDate,
    displayRange(value1, value2) {
      if (
        value1 === null ||
        value1 === undefined ||
        isNaN(value1) ||
        value2 === null ||
        value2 === undefined ||
        isNaN(value2)
      )
        return '-';
      return `${formatCurrencyShort(value1)} - ${formatCurrencyShort(value2)}`;
    },
    displayValue(value) {
      if (value === null || value === undefined || isNaN(value)) return '-';
      return value;
    },
    displayCurrentPeriod() {
      if (!this.competitorSummary) return '-';
      return this.displayDateRange(
        this.competitorSummary.current_period.start_date,
        this.competitorSummary.current_period.end_date,
      );
    },
    displayPreviousPeriod() {
      if (!this.competitorSummary) return '-';
      return this.displayDateRange(
        this.competitorSummary.previous_period.start_date,
        this.competitorSummary.previous_period.end_date,
      );
    },
    displayDateRange(startDate, endDate) {
      return `${formatStandardDate(startDate)} - ${formatStandardDate(endDate)}`;
    },
    updateDateRange(dates) {
      this.$emit('on-date-changed', dates);
    },
    onEditClicked() {
      this.$emit('on-edit-clicked');
    },
  },
  async created() {
    this.getCompetitorPerformanceSummary({
      competitorId: this.competitorId,
      startDate: this.dates[0],
      endDate: this.dates[1],
    });
  },
  async mounted() {
    bus.$on('refresh-bi-dashboard', () => {
      this.getCompetitorPerformanceSummary({
        competitorId: this.competitorId,
        startDate: this.dates[0],
        endDate: this.dates[1],
      });
    });
  },
  async beforeDestroy() {
    bus.$off('refresh-bi-dashboard');
  },
};
</script>

<style lang="scss">
.bi-competitor-summary {
  background-color: $color-white;
  padding: 24px;
  border-radius: $border-radius-root;
  margin-bottom: 24px;

  > .__header {
    display: flex;
    justify-content: flex-end;

    .__badge-container {
      margin-top: 4px;
      display: flex;

      .__competitor-badge {
        padding: 2px 16px;
        margin-bottom: 4px;
        align-items: center;
        color: $color-white;
        font-size: $font-size-small;
        background-color: $color-orange;
        border-radius: $border-radius-large;
      }
    }
  }

  > .__content {
    > div {
      padding: 16px;
      display: flex;
      margin-top: 16px;

      & > :first-child {
        width: 200px;
      }

      & > :last-child {
        flex-grow: 1;
      }

      &.__highlighted {
        background-color: $color-accent-pale;
        border-radius: $border-radius-root;
        border: 1px dashed $color-accent;
      }

      .__title {
        margin-right: 16px;
        font-weight: bold;
        font-size: $font-size-root;

        &.__highlighted {
          color: $color-accent;
        }
      }
    }
  }

  // Phones: as BISummary's card, the name above its figures.
  @include screen-below('tablet') {
    padding: 16px;

    > .__content > div {
      flex-direction: column;
      padding: 12px;

      & > :first-child {
        width: auto;
        margin-bottom: 12px;
      }
    }
  }
}
</style>
